import { GiftCard, GiftCardOrder, GiftCardPurchaseData } from '../types';
import { getPremiumPlanPrice } from '../config/pricing';

/**
 * Gift card management utilities
 */

export class GiftCardManager {
  private static instance: GiftCardManager;
  private giftCards: Map<string, GiftCard> = new Map();
  private orders: Map<string, GiftCardOrder> = new Map();

  private constructor() {
    this.loadFromStorage();
  }

  public static getInstance(): GiftCardManager {
    if (!GiftCardManager.instance) {
      GiftCardManager.instance = new GiftCardManager();
    }
    return GiftCardManager.instance;
  }

  /**
   * Generate a unique gift card code
   */
  private generateGiftCardCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    
    // Generate format: XXXX-XXXX-XXXX
    for (let i = 0; i < 12; i++) {
      if (i > 0 && i % 4 === 0) {
        result += '-';
      }
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // Ensure code is unique
    if (this.giftCards.has(result)) {
      return this.generateGiftCardCode();
    }
    
    return result;
  }

  /**
   * Get the price for a plan type
   */
  private getPlanPrice(planType: 'monthly' | 'yearly' | 'lifetime'): number {
    return getPremiumPlanPrice(planType);
  }

  /**
   * Get the duration for a plan type
   */
  private getPlanDuration(planType: 'monthly' | 'yearly' | 'lifetime'): number {
    switch (planType) {
      case 'monthly':
        return 1;
      case 'yearly':
        return 12;
      case 'lifetime':
        return 0; // 0 means lifetime
      default:
        return 0;
    }
  }

  /**
   * Create a gift card order
   */
  public createOrder(userId: string, purchaseData: GiftCardPurchaseData): GiftCardOrder {
    const orderId = `order_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const giftCards: GiftCard[] = [];

    const order: GiftCardOrder = {
      id: orderId,
      purchaserId: userId,
      quantity: purchaseData.quantity,
      planType: purchaseData.planType,
      totalAmount: purchaseData.totalAmount,
      paymentStatus: 'pending',
      giftCards,
      createdAt: new Date().toISOString()
    };

    this.orders.set(orderId, order);
    this.saveToStorage();
    
    return order;
  }

  /**
   * Complete a gift card order (simulate payment success)
   */
  public completeOrder(orderId: string): GiftCardOrder | null {
    const order = this.orders.get(orderId);
    if (!order) return null;

    if (order.paymentStatus === 'completed') {
      return order;
    }

    const generatedGiftCards: GiftCard[] = [];
    for (let i = 0; i < order.quantity; i++) {
      const giftCard: GiftCard = {
        id: `gc_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 11)}`,
        code: this.generateGiftCardCode(),
        planType: order.planType,
        value: this.getPlanPrice(order.planType),
        duration: this.getPlanDuration(order.planType),
        isRedeemed: false,
        createdAt: new Date().toISOString(),
        purchasedBy: order.purchaserId,
        expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      };

      generatedGiftCards.push(giftCard);
      this.giftCards.set(giftCard.code, giftCard);
    }

    order.paymentStatus = 'completed';
    order.completedAt = new Date().toISOString();
    order.giftCards = generatedGiftCards;
    
    this.orders.set(orderId, order);
    this.saveToStorage();
    
    return order;
  }

  public getOrder(orderId: string): GiftCardOrder | null {
    return this.orders.get(orderId) || null;
  }

  /**
   * Redeem a gift card code
   */
  public redeemGiftCard(code: string, userId: string): { success: boolean; message: string; giftCard?: GiftCard } {
    const giftCard = this.giftCards.get(code.toUpperCase());
    
    if (!giftCard) {
      return { success: false, message: 'Invalid gift card code' };
    }

    if (giftCard.isRedeemed) {
      return { success: false, message: 'This gift card has already been redeemed' };
    }

    if (giftCard.expiresAt && new Date(giftCard.expiresAt) < new Date()) {
      return { success: false, message: 'This gift card has expired' };
    }

    // Check if the user is trying to redeem their own gift card
    if (giftCard.purchasedBy === userId) {
      return { success: false, message: 'You cannot redeem your own gift card' };
    }

    // Redeem the gift card
    giftCard.isRedeemed = true;
    giftCard.redeemedBy = userId;
    giftCard.redeemedAt = new Date().toISOString();

    this.giftCards.set(code.toUpperCase(), giftCard);
    this.saveToStorage();

    return { 
      success: true, 
      message: `Successfully redeemed ${giftCard.planType} premium subscription!`,
      giftCard 
    };
  }

  /**
   * Get gift card by code
   */
  public getGiftCard(code: string): GiftCard | undefined {
    return this.giftCards.get(code.toUpperCase());
  }

  /**
   * Get all gift cards purchased by a user
   */
  public getUserGiftCards(userId: string): GiftCard[] {
    return Array.from(this.giftCards.values()).filter(gc => gc.purchasedBy === userId);
  }

  /**
   * Get all orders by a user
   */
  public getUserOrders(userId: string): GiftCardOrder[] {
    return Array.from(this.orders.values()).filter(order => order.purchaserId === userId);
  }

  /**
   * Validate gift card code format
   */
  public validateCodeFormat(code: string): boolean {
    const regex = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    return regex.test(code.toUpperCase());
  }

  /**
   * Save data to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem('typeRush_giftCards', JSON.stringify(Array.from(this.giftCards.entries())));
      localStorage.setItem('typeRush_giftCardOrders', JSON.stringify(Array.from(this.orders.entries())));
    } catch (error) {
      console.warn('Failed to save gift card data to localStorage:', error);
    }
  }

  /**
   * Load data from localStorage
   */
  private loadFromStorage(): void {
    try {
      const giftCardsData = localStorage.getItem('typeRush_giftCards');
      const ordersData = localStorage.getItem('typeRush_giftCardOrders');

      if (giftCardsData) {
        const entries = JSON.parse(giftCardsData);
        this.giftCards = new Map(entries);
      }

      if (ordersData) {
        const entries = JSON.parse(ordersData);
        this.orders = new Map(entries);
      }
    } catch (error) {
      console.warn('Failed to load gift card data from localStorage:', error);
    }
  }

  /**
   * Clear all data (for testing)
   */
  public clearAll(): void {
    this.giftCards.clear();
    this.orders.clear();
    localStorage.removeItem('typeRush_giftCards');
    localStorage.removeItem('typeRush_giftCardOrders');
  }
}

// Export singleton instance
export const giftCardManager = GiftCardManager.getInstance();
